import { extend, media, stylesheet } from 'typestyle';
import {
  BlackOpacity07,
  Blue80,
  Gold100,
  Gray100,
  Gray20,
  typeCompact10MediumProperties,
  typeCompact12MediumProperties,
  typeCompact14MediumProperties,
  typeCompact17MediumProperties,
  White,
} from '@traceair/tds';
import {
  BREAKPOINT_LARGE,
  BREAKPOINT_SMALL,
  CONTAINER_PADDINGS,
  MOBILE_CONTAINER_PADDINGS,
} from '../../../layout/styles';

export const containerCss = stylesheet({
  container: extend(
    {
      ...CONTAINER_PADDINGS,
      paddingTop: 6,
      paddingRight: 12,
      flex: '1 1 auto',
      overflow: 'auto',
      scrollbarGutter: 'stable',
    },
    media({ maxWidth: BREAKPOINT_LARGE }, { paddingRight: 22 }),
    media({ maxWidth: BREAKPOINT_SMALL }, { ...MOBILE_CONTAINER_PADDINGS }),
  ),
});

export const cardCss = stylesheet({
  card: {
    position: 'relative',
    padding: 8,
    border: `1px solid ${Gray20}`,
    borderRadius: 16,
    boxShadow: `0px 3px 10px ${BlackOpacity07}`,
    marginBottom: 6,
    cursor: 'pointer',
    $nest: {
      '&:hover': {
        boxShadow: '0px 3px 12px 0px rgba(0, 0, 0, 0.12)',
      },
    },
  },
  cardCompleted: {
    $nest: {
      '&&': {
        color: Gray100,
      },
    },
  },
  cardSelected: {
    $nest: {
      '&&': {
        border: `1px solid ${Blue80}`,
      },
    },
  },
  layout: extend(
    {
      display: 'flex',
      gap: 16,
      minHeight: 120,
      alignItems: 'stretch',
    },
    media(
      {
        maxWidth: BREAKPOINT_SMALL,
      },
      {
        minHeight: 96,
      },
    ),
  ),
  mainSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
    justifyContent: 'space-between',
    flex: '1 1 auto',
    padding: '6px 6px 6px 0',
  },
  updateIndicator: extend(
    {
      position: 'absolute',
      left: 16,
      top: 16,
      zIndex: 2,
    },
    media(
      { maxWidth: BREAKPOINT_SMALL },
      {
        display: 'none',
      },
    ),
  ),
  headerRow: {
    display: 'flex',
    gap: 16,
    justifyContent: 'space-between',
  },
  headerActions: extend(
    {
      display: 'flex',
      gap: 8,
      flexWrap: 'wrap',
      minWidth: '134px',
    },
    media(
      { maxWidth: 900 },
      {
        display: 'none',
      },
    ),
  ),
  siteName: extend(
    {
      margin: '0 0 4px',
      wordBreak: 'break-word',
      padding: 0,
      ...typeCompact17MediumProperties,
    },
    media(
      {
        maxWidth: BREAKPOINT_SMALL,
      },
      {
        ...typeCompact14MediumProperties,
      },
    ),
  ),
  titleGroup: extend({
    display: 'flex',
  }),
  winterConditionsMediumBadge: extend(
    {
      background: White,
      color: Gold100,
    },
    {
      $nest: {
        '&&': {
          border: 'none',
        },
      },
    },
    media(
      {
        maxWidth: BREAKPOINT_SMALL,
      },
      {
        display: 'none',
      },
    ),
  ),
  winterConditionsSmallBadge: extend(
    {
      background: White,
      color: Gold100,
      paddingLeft: 0,
      display: 'none',
    },
    {
      $nest: {
        '&&': {
          border: 'none',
        },
      },
    },
    media(
      {
        maxWidth: BREAKPOINT_SMALL,
      },
      {
        display: 'inline-flex',
      },
    ),
  ),
  customerName: extend(
    {
      wordBreak: 'break-word',
      color: Gray100,
      ...typeCompact14MediumProperties,
    },
    media(
      {
        maxWidth: BREAKPOINT_SMALL,
      },
      {
        ...typeCompact12MediumProperties,
      },
    ),
  ),
  initialFlightDate: extend(
    {
      position: 'absolute',
      right: 14,
      top: 45,
      display: 'flex',
      alignItems: 'center',
      gap: 4,
      color: Gray100,
      ...typeCompact12MediumProperties,
      $nest: {
        svg: {
          width: 16,
          height: 16,
        },
      },
    },
    media(
      {
        maxWidth: 900,
      },
      {
        position: 'static',
        marginTop: 5,
      },
    ),
    media(
      {
        maxWidth: BREAKPOINT_SMALL,
      },
      {
        marginTop: 3,
        gap: 2,
        ...typeCompact10MediumProperties,
        $nest: {
          svg: {
            width: 12,
            height: 12,
          },
        },
      },
    ),
  ),
  initialFlightDateLongModifier: extend(
    {
      display: 'flex',
    },
    media(
      {
        maxWidth: BREAKPOINT_SMALL,
      },
      {
        display: 'none',
      },
    ),
  ),
  initialFlightDateShortModifier: extend(
    {
      display: 'none',
    },
    media(
      {
        maxWidth: BREAKPOINT_SMALL,
      },
      {
        display: 'flex',
      },
    ),
  ),
  winterAndMovedDateContainer: extend(
    {
      display: 'inline-flex',
    },
    media(
      {
        maxWidth: BREAKPOINT_SMALL,
      },
      {
        $nest: {
          '& > :not([hidden]) ~ :not([hidden])::before': {
            content: '"•"',
            margin: '0 2px',
            lineHeight: 1,
          },
        },
      },
    ),
  ),
  missionInfo: {
    flex: '0 0 auto',
  },
  missionLabel: extend(
    {
      ...typeCompact14MediumProperties,
      color: Gray100,
      marginBottom: 6,
    },
    media(
      { maxWidth: 900 },
      {
        display: 'none',
      },
    ),
  ),
  footerRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-end',
    gap: 16,
  },
  badgeContainer: {
    display: 'flex',
    rowGap: 3,
    columnGap: 6,
    flexWrap: 'wrap',
  },
  badgeSmall: extend(
    {
      display: 'none',
    },
    media(
      {
        maxWidth: BREAKPOINT_SMALL,
      },
      {
        display: 'inline-flex',
      },
    ),
  ),
  badgeLarge: media(
    {
      maxWidth: BREAKPOINT_SMALL,
    },
    {
      display: 'none',
    },
  ),
  addressContainer: extend(
    {
      position: 'relative',
      height: 18,
      flex: '1 1 auto',
      maxWidth: 300,
      ...typeCompact12MediumProperties,
    },
    media(
      { maxWidth: 940 },
      {
        display: 'none',
      },
    ),
  ),
  addressContainerInProjects: extend(
    {
      $nest: {
        '&&': {
          display: 'block',
          maxWidth: '100%',
        },
      },
    },
    media(
      { minWidth: BREAKPOINT_SMALL },
      {
        ...typeCompact14MediumProperties,
      },
    ),
  ),
});
